// @ts-ignore
import '../css/style.css';

// ================================================
// All third-party, globally-required libraries should be loaded here
// ================================================

// Support for lazyload images (and bg images)
import 'lazysizes';
import 'lazysizes/plugins/unveilhooks/ls.unveilhooks';
import 'lazysizes/plugins/bgset/ls.bgset';
import 'lazysizes/plugins/attrchange/ls.attrchange';

// ================================================
// Setup Vue3
// ================================================
// @ts-ignore
import CollapseTransition from '@ivanv/vue-collapse-transition/src/CollapseTransition.vue';
import * as Sentry from '@sentry/vue';
// @ts-expect-error - no types
import vClickOutside from 'click-outside-vue3';
import {
    Pagination,
    Navigation,
    Autoplay,
    Grid,
} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { createApp, ref, defineAsyncComponent } from 'vue';

import { initAccordions } from './accordion';
import MainMenu from './components/MainMenu.vue';
import Rot13Email from './components/Rot13Email.vue';
import { initNavCrossfadeGsap } from './nav-crossfade-gsap';
import { initScrollAnimations } from './scroll-animations.js';

const app = createApp({
    delimiters: [ '${', '}' ],

    setup() {
        const swiperModules = ref([ Pagination, Navigation, Autoplay, Grid ]);
        const isMenuOpen = ref(false);
        const showAllPartners = ref(false);
        const governingBodyCategory = ref('all');

        const onToggleMenu = () => {
            isMenuOpen.value = !isMenuOpen.value;
        };

        return {
            swiperModules,
            isMenuOpen,
            onToggleMenu,
            showAllPartners,
            governingBodyCategory,
        };
    },
});

// Async: pulls Apollo + GraphQL into a separate chunk loaded only on pages
// that actually render a course listing (Courses provides its own client).
app.component('Courses', defineAsyncComponent(() => import('./components/Courses.vue')));
app.component('ShopSearch', defineAsyncComponent(() => import('./components/shop/ShopSearch.vue')));
app.component('AddToCart', defineAsyncComponent(() => import('./components/shop/AddToCart.vue')));
app.component('CartButton', defineAsyncComponent(() => import('./components/shop/CartButton.vue')));
app.component('Checkout', defineAsyncComponent(() => import('./components/shop/Checkout.vue')));
app.component('CartPage', defineAsyncComponent(() => import('./components/shop/CartPage.vue')));
app.component('MiniCart', defineAsyncComponent(() => import('./components/shop/MiniCart.vue')));
app.component('MainMenu', MainMenu);
app.component('Rot13Email', Rot13Email);
app.component('CollapseTransition', CollapseTransition);
app.component('Swiper', Swiper);
app.component('SwiperSlide', SwiperSlide);

app.use(vClickOutside);

app.mount('#app');
initScrollAnimations();
initAccordions();

if (process.env.NODE_ENV === 'production') {
    console.log(
        '%cProMED Tasmania',
        'font-size: 1.5rem; font-weight: 800; color: #014aad;',
    );
    console.log(
        '%cKeeping Tasmanians safe. Interested in joining the team? Visit promedtas.com.au/careers',
        'font-size: 0.875rem; color: #5c5d71;',
    );
}

// Floating pill nav — scroll behaviour + text width measurement
(function initFloatingNav() {
    const header = document.getElementById('site-header') as HTMLElement;

    if (!header) {
        return;
    }

    // Measure actual text widths for links that have short title variants.
    // Sets --text-width as a CSS custom property for pixel-perfect max-width transitions.
    function measureNavTextWidths() {
        const targets = header.querySelectorAll('[data-has-short-title] .nav-text-full, [data-has-short-title] .nav-text-short, .header__btn-text .nav-text-full, .header__btn-text .nav-text-short') as unknown as HTMLElement[];

        targets.forEach(span => {
            const prevMaxWidth = span.style.maxWidth;
            const prevOverflow = span.style.overflow;
            span.style.maxWidth = 'none';
            span.style.overflow = 'visible';

            const width = Math.ceil(span.getBoundingClientRect().width) + 4;
            span.style.setProperty('--text-width', `${width}px`);

            span.style.maxWidth = prevMaxWidth;
            span.style.overflow = prevOverflow;
        });
    }

    measureNavTextWidths();

    // GSAP trial — flip to false to revert to CSS transitions
    const USE_GSAP_NAV = true;
    const crossfade = USE_GSAP_NAV ? initNavCrossfadeGsap(header) : null;

    // On a cold load the initial measurement happens before the web font
    // (Figtree, display=swap) has downloaded, so widths are captured from the
    // wider fallback font and frozen onto each link. Re-measure once fonts are
    // ready. Resolves immediately (harmless re-measure) when already cached.
    document.fonts?.ready.then(() => {
        measureNavTextWidths();
        crossfade?.remeasure();
    });

    // Re-measure on resize (font rendering may change)
    let resizeTimer: ReturnType<typeof setTimeout>;

    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            measureNavTextWidths();
            crossfade?.remeasure();
        }, 200);
    }, { passive: true });

    // Scroll state toggle with hysteresis
    const SCROLL_DOWN_THRESHOLD = 80;
    const SCROLL_UP_THRESHOLD = 40;

    let isScrolled = false;
    let ticking = false;

    const update = () => {
        const y = window.scrollY;
        const shouldBeScrolled = isScrolled
            ? y > SCROLL_UP_THRESHOLD
            : y > SCROLL_DOWN_THRESHOLD;

        if (shouldBeScrolled !== isScrolled) {
            isScrolled = shouldBeScrolled;
            header.classList.toggle('site-header--scrolled', isScrolled);

            if (isScrolled) {
                crossfade?.play();
            } else {
                crossfade?.reverse();
            }
        }

        ticking = false;
    };

    window.addEventListener('scroll', () => {
        if (!ticking) {
            requestAnimationFrame(update);
            ticking = true;
        }
    }, { passive: true });

    update();

    // If page loaded already scrolled, snap GSAP to end state
    if (isScrolled) {
        crossfade?.play();
    }
})();

// Section sub-nav — scroll the pill so the current page's link is visible on narrow screens
(function centreCurrentSectionNavLink() {
    const current = document.querySelector<HTMLElement>('.section-nav__link--current');
    const list = current?.closest<HTMLElement>('.section-nav__list');

    if (!current || !list) {
        return;
    }

    const linkBox = current.getBoundingClientRect();
    const listBox = list.getBoundingClientRect();
    list.scrollLeft += (linkBox.left + linkBox.width / 2) - (listBox.left + listBox.width / 2);
})();

Sentry.init({
    app,
    dsn: process.env.SENTRY_DSN,
    integrations: [
        Sentry.httpClientIntegration(),
        Sentry.captureConsoleIntegration({ levels: [ 'error' ] }),
    ],
    tracesSampleRate: 0.2,
    tracePropagationTargets: [ 'promedtas.com.au' ],
});

document.addEventListener('onFormieLoaded', () => {
    const repeaters = document.querySelectorAll('[data-field-type="repeater"]') as unknown as HTMLElement[];

    repeaters.forEach((el: HTMLElement) => {
        const addButton = el.querySelector('.fui-repeater-add-btn') as HTMLElement;

        // @ts-ignore
        el.addEventListener('initRow', (event: CustomEvent) => {
            const row = event.detail.$row;

            const removeButton = row.querySelector('.fui-repeater-remove-btn') as HTMLElement;

            const itemNames = addButton.textContent.split(' ');
            if (itemNames.length === 1) {
                return;
            }

            const itemName = itemNames[itemNames.length - 1];

            removeButton.textContent = `Remove ${itemName}`;
        });

        const confirmText = el.getAttribute('data-add-confirm');

        if (confirmText) {
            addButton.addEventListener('click', event => {
                if (!confirm(confirmText)) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            }, true);
        }
    });
});
